import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  createTheme,
  TextField,
  MenuItem,
  InputAdornment,
  TableBody,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Pagination,
  DialogContent,
  Dialog,
  DialogTitle,
  DialogActions,
  Paper,
  Checkbox,
  ListItemSecondaryAction,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  Check as CheckIcon,
  Search,
  Close as CloseIcon,
  Delete,
  Edit,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { userManagement } from "../../config/images";
import {
  getPlanList,
  addPlan,
  UpdatePlanById,
  deletePlanById,
  updatePlanStatus,
  userSubscription,
  deleteUserSubscription,
  userPayment,
  deleteUserPayment,
  userSubscriptionId,
  userPaymentId,
} from "./services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Subscription = () => {
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openUserViewDialog, setOpenUserViewDialog] = useState(false);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [showEditPlan, setShowEditPlan] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [paymentLogs, setPaymentLogs] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [errorSubscriptions, setErrorSubscriptions] = useState(null);
  const [errorPayments, setErrorPayments] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentPageSize, setPaymentPageSize] = useState(5);
  const [paymentTotalCount, setPayemntTotalCount] = useState(0);
  // New state for view details dialog
  const [currentViewDetails, setCurrentViewDetails] = useState(null);
  const [loadingViewDetails, setLoadingViewDetails] = useState(false);
  const [errorViewDetails, setErrorViewDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // New state for the debounced search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // useEffect to handle debouncing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms debounce delay

    // Cleanup function to clear the timeout if searchTerm changes
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]); // Rerun this effect whenever searchTerm changes
  const handlePageChange = (event, value) => {
    setPage(value);
  };
  const handlePaymentPageChange = (event, value) => {
    setPaymentPage(value);
  };
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await getPlanList();
        if (response.data && Array.isArray(response.data.payload)) {
          setPlans(response.data.payload);
        } else {
          setError("Failed to fetch plans or invalid data format.");
          setPlans([]);
        }
      } catch (err) {
        setError("Failed to fetch plans.");
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    // Modified to use the debounced search term

    fetchPlans();
  }, []); // The dependency array now uses debouncedSearchTerm

  useEffect(() => {
    fetchPaymentLogs(); // This will now only run when debouncedSearchTerm changes
  }, [paymentPageSize, paymentPage]);

  const fetchPaymentLogs = async () => {
    try {
      const options = {
        page: paymentPage,
        page_size: paymentPageSize,
      };
      setLoadingPayments(true)
      const response = await userPayment(options);
      if (response.data && Array.isArray(response.data.results.payload)) {
        setPaymentLogs(response.data.results.payload);
        setPayemntTotalCount(response.data.count);
      } else {
        setErrorPayments(
          "Failed to fetch payment logs or invalid data format."
        );
        setPaymentLogs([]);
      }
    } catch (err) {
      setErrorPayments("Failed to fetch payment logs.");
      setPaymentLogs([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  useEffect(() => {
    fetchUserSubscriptions(); // This will now only run when debouncedSearchTerm changes
  }, [debouncedSearchTerm, statusFilter, pageSize, page]);

  const fetchUserSubscriptions = async () => {
    try {
      setLoadingSubscriptions(true);
      const options = {
        search: debouncedSearchTerm,
        status: statusFilter === "All" ? "" : statusFilter.toLowerCase(),
        page: page,
        page_size: pageSize,
      };
      const response = await userSubscription(options);
      if (response.data && Array.isArray(response.data.results.payload)) {
        setUserSubscriptions(response.data.results.payload);
        setTotalCount(response.data.count);
      } else {
        setErrorSubscriptions(
          "Failed to fetch subscriptions or invalid data format."
        );
        setUserSubscriptions([]);
      }
    } catch (err) {
      setErrorSubscriptions("Failed to fetch user subscriptions.");
      setUserSubscriptions([]);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleCreatePlanClick = () => {
    setShowCreatePlan(true);
    setShowEditPlan(false);
    setCurrentPlan(null);
  };

  const handleViewOrEditPlanClick = (plan) => {
    setCurrentPlan({
      ...plan,
      planType: plan.billing_cycle,
      isBeingEdited: true, // This line was updated to always set isBeingEdited to true
    });
    setShowEditPlan(true);
    setShowCreatePlan(false);
  };

  const handleViewPlanClick = (plan) => {
    setCurrentPlan({
      ...plan,
      planType: plan.billing_cycle,
      isBeingEdited: false,
    });
    setShowEditPlan(true);
    setShowCreatePlan(false);
  };

  const handleCancelAction = () => {
    setShowCreatePlan(false);
    setShowEditPlan(false);
    setCurrentPlan(null);
  };

  const handleOpenDeleteDialog = (item, type) => {
    if (type === "plan") {
      setCurrentPlan(item);
    } else if (type === "subscription") {
      setCurrentSubscription(item);
    } else if (type === "payment") {
      setCurrentPayment(item);
    }
    setDeleteType(type);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setCurrentPlan(null);
    setCurrentSubscription(null);
    setCurrentPayment(null);
    setDeleteType(null);
  };

  const handleOpenUserViewDialog = async (recordId, recordType) => {
    setLoadingViewDetails(true);
    setErrorViewDetails(null);
    setCurrentViewDetails(null);
    setOpenUserViewDialog(true);

    try {
      let response;
      if (recordType === "subscription") {
        response = await userSubscriptionId(recordId);
      } else if (recordType === "payment") {
        response = await userPaymentId(recordId);
      }

      if (response && response.data && response.data.payload) {
        setCurrentViewDetails({ ...response.data.payload, recordType });
      } else {
        setErrorViewDetails(
          `Failed to fetch details for ${recordType} or invalid data.`
        );
      }
    } catch (err) {
      setErrorViewDetails(`Failed to fetch ${recordType} details.`);
      console.error("API Error:", err);
    } finally {
      setLoadingViewDetails(false);
    }
  };

  const handleCloseUserViewDialog = () => {
    setOpenUserViewDialog(false);
    setCurrentViewDetails(null);
  };

  const handleTogglePlanStatus = async () => {
    if (!currentPlan) return;
    try {
      const newStatus = !currentPlan.is_active;
      const response = await updatePlanStatus({
        id: currentPlan.id,
        is_active: newStatus,
      });

      if (response.status === 200) {
        setPlans((prevPlans) =>
          prevPlans.map((plan) =>
            plan.id === currentPlan.id
              ? { ...plan, is_active: newStatus }
              : plan
          )
        );
        setCurrentPlan((prev) => ({ ...prev, is_active: newStatus }));
        toast.success(
          `Plan ${newStatus ? "activated" : "deactivated"} successfully!`,{toastId:"update-success"}
        );
      } else {
        toast.error("Failed to update plan status.",{toastId:"update-error"});
      }
    } catch (err) {
      toast.error("Failed to update plan status.",{toastId:"update-error"});
      console.error("API Error:", err);
    } finally {
      handleCloseStatusDialog();
    }
  };

  const handleOpenStatusDialog = (plan) => {
    setCurrentPlan(plan);
    setOpenStatusDialog(true);
  };

  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false);
  };

  const PlanFormUI = ({ initialPlanData, onCancel, onSubmit }) => {
    const [planName, setPlanName] = useState(initialPlanData?.name || "");
    const [planType, setPlanType] = useState(initialPlanData?.planType || "");
    const [price, setPrice] = useState(initialPlanData?.price || "");
    const [originalPrice, setOriginalPrice] = useState(
      initialPlanData?.original_price || ""
    );
    const [features, setFeatures] = useState(initialPlanData?.features || []);
    const [newFeature, setNewFeature] = useState("");
    const [editingFeatureIndex, setEditingFeatureIndex] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    React.useEffect(() => {
      if (initialPlanData && !initialPlanData.isBeingEdited) {
        setIsEditing(false);
      } else if (initialPlanData && initialPlanData.isBeingEdited) {
        setIsEditing(true);
      } else {
        setIsEditing(true);
      }
    }, [initialPlanData]);

    const handleAddOrUpdateFeature = () => {
      if (newFeature.trim() !== "") {
        if (editingFeatureIndex !== null) {
          // Update existing feature
          const updatedFeatures = [...features];
          updatedFeatures[editingFeatureIndex] = {
            ...updatedFeatures[editingFeatureIndex],
            feature_text: newFeature,
          };
          setFeatures(updatedFeatures);
          setEditingFeatureIndex(null);
        } else {
          // Add new feature (no id)
          setFeatures([...features, { feature_text: newFeature }]);
        }
        setNewFeature("");
      }
    };

    const handleEditFeature = (index) => {
      const featureToEdit = features[index];
      setNewFeature(featureToEdit.feature_text);
      setEditingFeatureIndex(index);
    };

    const handleDeleteFeature = (index) => {
      const updated = [...features];
      updated.splice(index, 1);
      setFeatures(updated);
    };

    const handleSubmit = () => {
      const updatedPlan = {
        id: initialPlanData?.id,
        name: planName,
        planType: planType,
        price: price,
        original_price: originalPrice,
        features: features,
        is_active: initialPlanData ? initialPlanData.is_active : true,
      };
      onSubmit(updatedPlan);
    };

    return (
      <Grid container spacing={2} sx={{ width: "100%", margin: 0 }}>
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ paddingLeft: "8px !important", paddingTop: "8px !important" }}
        >
          <Box
            sx={{
              background: "#ffffff",
              padding: "30px",
              border: "1px solid #1570EF",
              borderRadius: "8px",
              height: "100%",
              minHeight: "600px",
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {initialPlanData && !isEditing
                ? "Plan Details View"
                : initialPlanData && isEditing
                ? "Update Plan"
                : "Create New Plan"}
            </Typography>
            <Box display="flex" gap={2} mt={2}>
              <TextField
                label="Plan Name"
                placeholder="e.g. professional plan"
                fullWidth
                margin="normal"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                disabled={!isEditing}
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#000000",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isEditing ? undefined : "#E5E5E5",
                  },
                  "& .MuiInputLabel-root.Mui-disabled": {
                    color: "rgba(0, 0, 0, 0.6)",
                  },
                }}
              />
              <TextField
                select
                label="Plan Type"
                placeholder="Select Plan"
                fullWidth
                margin="normal"
                value={planType}
                onChange={(e) => setPlanType(e.target.value)}
                disabled={!isEditing}
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#000000",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isEditing ? undefined : "#E5E5E5",
                  },
                  "& .MuiInputLabel-root.Mui-disabled": {
                    color: "rgba(0, 0, 0, 0.6)",
                  },
                }}
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>
            </Box>
            <Box display="flex" gap={2} mt={2}>
              <TextField
                label="Price (₹)"
                placeholder="Enter price"
                fullWidth
                margin="normal"
                value={price}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {  // only digits allowed
                    setPrice(value);
                  }
                }}
                disabled={!isEditing}
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#000000",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isEditing ? undefined : "#E5E5E5",
                  },
                  "& .MuiInputLabel-root.Mui-disabled": {
                    color: "rgba(0, 0, 0, 0.6)",
                  },
                }}
              />
              <TextField
                label="Original Price (₹)"
                placeholder="Enter original price"
                fullWidth
                margin="normal"
                value={originalPrice}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {  // only digits allowed
                    setOriginalPrice(value);
                  }
                }}
                disabled={!isEditing}
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#000000",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isEditing ? undefined : "#E5E5E5",
                  },
                  "& .MuiInputLabel-root.Mui-disabled": {
                    color: "rgba(0, 0, 0, 0.6)",
                  },
                }}
              />
            </Box>
            {isEditing && (
              <Box display="flex" gap={0} alignItems="center" mt={2}>
                <TextField
                  label="Add/Update Features"
                  placeholder="Add features"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddOrUpdateFeature();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddOrUpdateFeature}
                  sx={{ padding: "16px!important", background: "#1570EF" }}
                >
                  {editingFeatureIndex !== null ? "Update" : "Add"}
                </Button>
              </Box>
            )}
            <Typography variant="subtitle1" fontWeight="bold" mt={3}>
              Plan Features List
            </Typography>
            <List>
              {features.map((feature, index) => (
                <ListItem key={index} sx={{alignItems: 'start'}}>
                  <Checkbox
                    checked
                    size="small"
                    sx={{ p: 0, mr: 1,mt: 0.5 }}
                    disabled={!isEditing}
                  />
                  <ListItemText primary={feature.feature_text}
                    primaryTypographyProps={{
                      variant: "body2",
                      sx: {
                        whiteSpace: "normal", // allow wrapping
                        wordBreak: "break-word", // break long words if needed
                        paddingRight:"40px"
                      },
                    }}
                  />
                  {isEditing && (
                    <ListItemSecondaryAction sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        color="primary"
                        size="small"
                        onClick={() => handleEditFeature(index)}
                      >
                        <img src={userManagement.editUser} alt="Edit" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteFeature(index)}
                      >
                        <img src={userManagement.deleteUser} alt="Delete" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        </Grid>
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ paddingLeft: "8px !important", paddingTop: "8px !important" }}
        >
          <Box
            sx={{
              background: "#ffffff",
              padding: "30px",
              border: "1px solid #1570EF",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "600px",
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              PLAN PREVIEW
            </Typography>
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>
                  ₹{price || "0"}/{planType.toLowerCase() || "monthly"}
                </Typography>
                <List dense>
                  {features.map((feature, index) => (
                    <ListItem
                      key={feature.id || index}
                      disableGutters
                      sx={{ py: 0.5, alignItems:"start" }}
                    >
                      <ListItemIcon sx={{ minWidth: "30px",mt:0.5 }}>
                        <CheckCircleOutlineIcon
                          fontSize="small"
                          sx={{ color: "#1570EF" }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.feature_text}
                        primaryTypographyProps={{
                          variant: "body2",
                          sx: {
                            color: "black",
                            whiteSpace: "normal", // allow wrapping
                            wordBreak: "break-word", // break long words if needed
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
              <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  sx={{
                    textTransform: "capitalize !important",
                    padding: "10px 16px !important",
                    boxShadow: "none",
                  }}
                >
                  Back
                </Button>
                {isEditing && (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    sx={{
                      textTransform: "capitalize !important",
                      background: "#1570EF",
                      padding: "10px 16px !important",
                      boxShadow: "none",
                    }}
                  >
                    {initialPlanData ? "Update Plan" : "Create Plan"}
                  </Button>
                )}
                {initialPlanData && !isEditing && (
                  <Button
                    variant="contained"
                    onClick={() => handleOpenStatusDialog(initialPlanData)}
                    sx={{
                      textTransform: "capitalize !important",
                      padding: "10px 16px !important",
                      boxShadow: "none",
                      color: "#1570EF",
                      backgroundColor: "transparent",
                      border: "1px solid #1570EF",
                    }}
                  >
                    {initialPlanData.is_active ? "Deactivate" : "Activate"} this
                    plan
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const handleCreatePlanSubmit = async (newPlan) => {
    try {
      const formattedFeatures = newPlan.features.map((feature) => ({
        feature_text: feature.feature_text,
      }));

      const newPlanForAPI = {
        name: newPlan.name,
        plan_type: newPlan.planType,
        amount: newPlan.price,
        features: formattedFeatures,
        original_price: newPlan.original_price,
      };

      const response = await addPlan(newPlanForAPI);

      if (response.data && response.data.payload) {
        setPlans((prevPlans) => [...prevPlans, response.data.payload]);
        toast.success(response.data.message || "Plan created successfully!",{toastId:"create-success"});
        handleCancelAction();
      } else {
        toast.error(response.data.message || "Failed to create plan: Invalid response.",{toastId:"create-error"});
      }
    } catch (err) {
      console.log(err)
      toast.error(err.response.data.message || "Failed to create plan.",{toastId:"create-error"});
      console.error("API Error:", err);
    }
  };

  const handleUpdatePlanSubmit = async (updatedPlan) => {
    try {
      const formattedFeatures = updatedPlan.features.map((feature) => {
        const formatted = { feature_text: feature.feature_text };
        if (feature.id) {
          formatted.id = feature.id;
        }
        return formatted;
      });

      const updatedPlanForAPI = {
        name: updatedPlan.name,
        plan_type: updatedPlan.planType,
        amount: updatedPlan.price,
        features: formattedFeatures,
      };

      const response = await UpdatePlanById(updatedPlan.id, updatedPlanForAPI);

      if (response.data && response.data.payload) {
        setPlans((prevPlans) =>
          prevPlans.map((plan) =>
            plan.id === updatedPlan.id ? response.data.payload : plan
          )
        );
        toast.success("Plan updated successfully!",{toastId:"update-success"});
        handleCancelAction();
      } else {
        toast.error("Failed to update plan: Invalid response.",{toastId:"update-error"});
      }
    } catch (err) {
      toast.error("Failed to update plan.",{toastId:"update-error"});
      console.error("API Error:", err);
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteType === "plan") {
        if (currentPlan && currentPlan.id) {
          await deletePlanById(currentPlan.id);
          setPlans((prevPlans) =>
            prevPlans.filter((plan) => plan.id !== currentPlan.id)
          );
          toast.success("Plan deleted successfully!",{toastId:"plan-delete-success"});
        }
      } else if (deleteType === "subscription") {
        if (currentSubscription && currentSubscription.id) {
          await deleteUserSubscription(currentSubscription.id);
          setUserSubscriptions((prevSubs) =>
            prevSubs.filter((sub) => sub.id !== currentSubscription.id)
          );
          toast.success("User subscription deleted successfully!",{toastId:"sub-delete-success"});
        }
      } else if (deleteType === "payment") {
        if (currentPayment && currentPayment.id) {
          await deleteUserPayment(currentPayment.id);
          setPaymentLogs((prevPayments) =>
            prevPayments.filter((payment) => payment.id !== currentPayment.id)
          );
          toast.success("User payment deleted successfully!",{toastId:"payment-delete-success"});
        }
      }
    } catch (err) {
      toast.error("Failed to delete item.",{toastId:"delete-error"});
      console.error("API Error:", err);
    } finally {
      handleCloseDeleteDialog();
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#eaf3fc",
        minHeight: "94vh",
        padding: { xs: "14px", md: "36px" },
      }}
    >
     
      {showCreatePlan ? (
        <PlanFormUI
          onCancel={handleCancelAction}
          onSubmit={handleCreatePlanSubmit}
          initialPlanData={null}
        />
      ) : showEditPlan && currentPlan ? (
        <PlanFormUI
          initialPlanData={currentPlan}
          onCancel={handleCancelAction}
          onSubmit={handleUpdatePlanSubmit}
        />
      ) : (
        <>
          <Box
            sx={{
              background: "#ffffff",
              padding: "30px",
              border: "1px solid #1570EF",
              borderRadius: "8px",
            }}
          >
            <Box
              display="flex"
              flexWrap="wrap"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              mb={3}
            >
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: "24px", md: "36px", sm: "32px" },
                  fontWeight: 600,
                }}
              >
                Subscription Management
              </Typography>
              <Box>
                <button
                  className="api-integration-btn"
                  onClick={handleCreatePlanClick}
                  style={{
                    backgroundColor: "#1570EF",
                    color: "#FFFFFF",
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Create Plan
                </button>
              </Box>
            </Box>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <Typography color="error">{error}</Typography>
              </Box>
            ) : plans.length < 1 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <Typography>No Plans Found!</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  overflowX: "auto",
                  pb: 2,
                  "&::-webkit-scrollbar": { height: "8px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#cbd5e1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f5e9",
                  },
                }}
              >
                <Grid
                  container
                  spacing={4}
                  sx={{ flexWrap: "nowrap", width: "fit-content" }}
                >
                  {plans.map((plan, index) => (
                    <Grid item xs={12} md={4} key={index}>
                      <Card
                        sx={{
                          height: "100%",
                          width: 400,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between", // ✅ footer always at bottom
                          backgroundColor: plan.is_active
                            ? "#175CD3"
                            : "#5a8adb",
                          color: "#ffffff",
                          border: `1px solid ${
                            plan.is_active ? "#5a8adb" : "#A0AEC0"
                          }`,
                          borderRadius: "12px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          opacity: plan.is_active ? 1 : 0.7,
                        }}
                      >
                        <CardContent
                          sx={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                            padding: "10px 32px",
                          }}
                        >
                          {/* Header with title + icons */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "start",
                              mt: 1.5,
                            }}
                          >
                            <Typography
                              variant="overline"
                              sx={{
                                color: "#ffffff",
                                fontSize: "15px",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                              }}
                            >
                              {plan.name}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <IconButton
                                size="small"
                                sx={{ color: "#ffffff" }}
                                onClick={() =>
                                  handleViewOrEditPlanClick({
                                    ...plan,
                                    isBeingEdited: true,
                                  })
                                }
                              >
                                <img
                                  src={userManagement.editUserWhite}
                                  alt="Edit"
                                />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{ color: "#ffffff" }}
                                onClick={() =>
                                  handleOpenDeleteDialog(plan, "plan")
                                }
                              >
                                <img
                                  src={userManagement.deleteUserWhite}
                                  alt="Delete"
                                />
                              </IconButton>
                            </Box>
                          </Box>

                          {/* Price */}
                          <Box sx={{ mt: 2, mb: 2 }}>
                            <Box
                              sx={{ display: "flex", alignItems: "baseline" }}
                            >
                              {plan.original_price && (
                                <Typography
                                  variant="h6"
                                  sx={{
                                    textDecoration: "line-through",
                                    color: "#ffffff",
                                    mr: 1,
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  ₹{plan.original_price}
                                </Typography>
                              )}
                              <Typography
                                variant="h4"
                                component="div"
                                sx={{ color: "#ffffff", fontWeight: "bold" }}
                              >
                                ₹{plan.price}
                              </Typography>
                              <Typography
                                variant="subtitle1"
                                sx={{ color: "#ffffff", ml: 0.5 }}
                              >
                                /
                                {plan.billing_cycle === "monthly"
                                  ? "Month"
                                  : plan.billing_cycle === "weekly"
                                  ? "Week"
                                  : plan.billing_cycle === "quarterly"
                                  ? "Quarter"
                                  : plan.billing_cycle === "yearly"
                                  ? "Year"
                                  : plan.billing_cycle}
                              </Typography>
                            </Box>
                          </Box>

                          <Divider sx={{ borderColor: "#ffffff", mb: 1 }} />

                          {/* Features */}
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#ffffff", mb: 1, fontWeight: 500 }}
                            >
                              All Free features, plus:
                            </Typography>
                            <List dense>
                              {plan.features.slice(0, 5).map((feature, idx) => (
                                <ListItem
                                  key={idx}
                                  disableGutters
                                  sx={{ py: 0.5, alignItems:"flex-start" }} 
                                >
                                  <ListItemIcon sx={{ minWidth: "30px" }}>
                                    <CheckIcon
                                      fontSize="small"
                                      sx={{
                                        color: "#1570EF",
                                        backgroundColor: "#ffffff",
                                        borderRadius: "50%",
                                        padding: "2px",
                                        fontSize: "16px",
                                        mt: 0.8,
                                      }}
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={feature.feature_text}
                                    primaryTypographyProps={{
                                      variant: "body2",
                                      sx: {
                                        color: "#ffffff",
                                        whiteSpace: "normal", // allow wrapping
                                        wordBreak: "break-word", // break long words if needed
                                      },
                                    }}
                                  />
                                </ListItem>
                              ))}
                              {plan.features.length > 5 && (
                                <ListItem disableGutters sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: "30px" }}>
                                    <CheckIcon
                                      fontSize="small"
                                      sx={{
                                        color: "#1570EF",
                                        backgroundColor: "#ffffff",
                                        borderRadius: "50%",
                                        padding: "2px",
                                        fontSize: "16px",
                                      }}
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary="And more!"
                                    primaryTypographyProps={{
                                      variant: "body2",
                                      sx: { color: "#ffffff" },
                                    }}
                                  />
                                </ListItem>
                              )}
                            </List>
                          </Box>

                          {/* Footer (always bottom) */}
                          <Box
                            sx={{
                              borderTop: "1px solid #ffffff",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              pt: 2,
                              mt: "auto",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "#ffffff", textAlign: "left" }}
                            >
                              {plan.active_users_count ? plan.active_users_count : "0"} Active Users
                            </Typography>
                            <Button
                              variant="outlined"
                              onClick={() => handleViewPlanClick(plan)}
                              sx={{
                                color: "#ffffff",
                                borderColor: "#ffffff",
                                textTransform: "none",
                                borderRadius: "6px",
                                padding: "6px 16px",
                                "&:hover": { borderColor: "#ffffff" },
                              }}
                            >
                              View Details
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                justifyContent: "space-between",
                mb: 2,
                gap: 2,
                mt: 4,
              }}
            >
              <TextField
                size="small"
                placeholder="Search Users"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  maxWidth: { md: 500 },
                  bgcolor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                }}
              />

              {/* Buttons */}
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                <TextField
                  select
                  size="small"
                  sx={{ minWidth: 120 }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="All">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Canceled">Canceled</MenuItem>
                  <MenuItem value="Expired">Expired</MenuItem>
                </TextField>
              </Box>
            </Box>
            <Typography
              variant="h6"
              mb={{ xs: 2, sm: 0 }}
              sx={{ mb: 1, mt: 4 }}
            >
              User Subscriptions
            </Typography>
            <Box sx={{ mb: 3 }}>
              <TableContainer
                sx={{ overflowX: "auto", border: "1px solid #e0e0e0" }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <b>USER</b>
                      </TableCell>
                      <TableCell>
                        <b>EMAIL</b>
                      </TableCell>
                      <TableCell>
                        <b>CURRENT PLAN</b>
                      </TableCell>
                      <TableCell>
                        <b>BILLING CYCLE</b>
                      </TableCell>
                      <TableCell>
                        <b>STATUS</b>
                      </TableCell>
                      <TableCell>
                        <b>ACTIONS</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingSubscriptions ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : errorSubscriptions ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="error">
                            {errorSubscriptions}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : userSubscriptions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography>No user subscriptions found.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      userSubscriptions.map((subscription) => (
                        <TableRow key={subscription.id}>
                          <TableCell>{subscription?.full_name}</TableCell>
                          <TableCell>{subscription?.email}</TableCell>
                          <TableCell>{subscription?.current_plan}</TableCell>
                          <TableCell>{subscription?.billing_cycle}</TableCell>
                          <TableCell>
                            <Chip
                              label={subscription.status}
                              size="small"
                              sx={{
                                bgcolor:
                                  subscription.status === "Active"
                                    ? "rgba(0,200,83,0.1)"
                                    : "rgba(255,0,0,0.1)",
                                color:
                                  subscription.status === "Active"
                                    ? "green"
                                    : "red",
                                fontWeight: 600,
                                borderRadius: "6px",
                                height: "22px",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: "15px" }}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenUserViewDialog(
                                    subscription.id,
                                    "subscription"
                                  )
                                }
                                color="primary"
                              >
                                <img
                                  src={userManagement.viewUser}
                                  alt="viewUser"
                                />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenDeleteDialog(
                                    subscription,
                                    "subscription"
                                  )
                                }
                                color="error"
                              >
                                <img
                                  src={userManagement.deleteUser}
                                  alt="Delete"
                                />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={handlePageChange}
                shape="rounded"
                sx={{ float: "right", mt: 2 }}
              />
            </Box>
            <Typography
              variant="h6"
              mb={{ xs: 2, sm: 0 }}
              sx={{ mb: 1, mt: 4 }}
            >
              Payment logs
            </Typography>
            <Box sx={{ mb: 3 }}>
              <TableContainer
                sx={{ overflowX: "auto", border: "1px solid #e0e0e0" }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <b>TRANSACTION ID</b>
                      </TableCell>
                      <TableCell>
                        <b>USER</b>
                      </TableCell>
                      <TableCell>
                        <b>AMOUNT</b>
                      </TableCell>
                      <TableCell>
                        <b>DATE</b>
                      </TableCell>
                      <TableCell>
                        <b>METHOD</b>
                      </TableCell>
                      <TableCell>
                        <b>STATUS</b>
                      </TableCell>
                      <TableCell>
                        <b>ACTIONS</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingPayments ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : errorPayments ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="error">{errorPayments}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : paymentLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography>No payment logs found.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paymentLogs.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{payment.transaction_id}</TableCell>
                          <TableCell>{payment?.full_name}</TableCell>
                          <TableCell>{payment.price}</TableCell>
                          <TableCell>{payment.created_at}</TableCell>
                          <TableCell>{payment.payment_method}</TableCell>
                          <TableCell>
                            <Chip
                              label={payment.status}
                              size="small"
                              sx={{
                                bgcolor:
                                  payment.status === "Success"
                                    ? "rgba(0,200,83,0.1)"
                                    : "rgba(255,0,0,0.1)",
                                color:
                                  payment.status === "Success"
                                    ? "green"
                                    : "red",
                                fontWeight: 600,
                                borderRadius: "6px",
                                height: "22px",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: "15px" }}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenUserViewDialog(
                                    payment.id,
                                    "payment"
                                  )
                                }
                                color="primary"
                              >
                                <img
                                  src={userManagement.viewUser}
                                  alt="viewUser"
                                />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenDeleteDialog(payment, "payment")
                                }
                                color="error"
                              >
                                <img
                                  src={userManagement.deleteUser}
                                  alt="Delete"
                                />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Pagination
                count={Math.ceil(paymentTotalCount / paymentPageSize)}
                page={paymentPage}
                onChange={handlePaymentPageChange}
                shape="rounded"
                sx={{ float: "right", mt: 2 }}
              />
            </Box>
          </Box>
        </>
      )}
      <Dialog
        open={openUserViewDialog}
        sx={{
          "& .MuiDialog-paper": {
            width: "450px",
            maxWidth: "450px",
            borderRadius: "12px",
          },
        }}
        onClose={handleCloseUserViewDialog}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
            fontSize: "18px",
            pb: 1,
            borderBottom: "1px solid #E5E5E5",
          }}
        >
          View Details
          <IconButton
            onClick={handleCloseUserViewDialog}
            size="small"
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {loadingViewDetails ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "150px",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : errorViewDetails ? (
            <Typography color="error">{errorViewDetails}</Typography>
          ) : currentViewDetails &&
            currentViewDetails.recordType === "subscription" ? (
            <Box>
              <Box sx={{ display: "flex", mb: 1 }}>
                <Typography variant="body1" sx={{ minWidth: "120px" }}>
                  User Name:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {currentViewDetails?.full_name}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", mb: 1 }}>
                <Typography variant="body1" sx={{ minWidth: "120px" }}>
                  Email:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {currentViewDetails?.email}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", mb: 1 }}>
                <Typography variant="body1" sx={{ minWidth: "120px" }}>
                  Current Plan:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {currentViewDetails.current_plan}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", mb: 1 }}>
                <Typography variant="body1" sx={{ minWidth: "120px" }}>
                  Billing Cycle:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {currentViewDetails.billing_cycle}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", mb: 1 }}>
                <Typography variant="body1" sx={{ minWidth: "120px" }}>
                  Status:
                </Typography>
                <Chip
                  label={currentViewDetails.status}
                  size="small"
                  sx={{
                    bgcolor:
                      currentViewDetails.status === "Active"
                        ? "rgba(0,200,83,0.1)"
                        : "rgba(255,0,0,0.1)",
                    color:
                      currentViewDetails.status === "Active" ? "green" : "red",
                    fontWeight: 600,
                    borderRadius: "6px",
                    height: "22px",
                  }}
                />
              </Box>
            </Box>
          ) : currentViewDetails &&
            currentViewDetails.recordType === "payment" ? (
            <Box>
              <Box sx={{ display: "flex", mb: 1 }}>
                <Typography variant="body1" sx={{ minWidth: "120px" }}>
                  Transaction ID:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {currentViewDetails.transaction_id}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", mb: 1 }}>
                <Typography variant="body1" sx={{ minWidth: "120px" }}>
                  User Name:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {currentViewDetails?.full_name}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", mb: 1 }}>
                <Typography variant="body1" sx={{ minWidth: "120px" }}>
                  Amount:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {currentViewDetails.price}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", mb: 1 }}>
                <Typography variant="body1" sx={{ minWidth: "120px" }}>
                  Date:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {currentViewDetails.created_at}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", mb: 1 }}>
                <Typography variant="body1" sx={{ minWidth: "120px" }}>
                  Method:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {currentViewDetails.payment_method}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", mb: 1 }}>
                <Typography variant="body1" sx={{ minWidth: "120px" }}>
                  Status:
                </Typography>
                <Chip
                  label={currentViewDetails.status}
                  size="small"
                  sx={{
                    bgcolor:
                      currentViewDetails.status === "Success"
                        ? "rgba(0,200,83,0.1)"
                        : "rgba(255,0,0,0.1)",
                    color:
                      currentViewDetails.status === "Success" ? "green" : "red",
                    fontWeight: 600,
                    borderRadius: "6px",
                    height: "22px",
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Typography>Select an item to view details.</Typography>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        sx={{
          "& .MuiDialog-paper": {
            width: "450px",
            maxWidth: "450px",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontSize: "24px",
            fontWeight: "600",
            mt: 1.5,
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", my: 2 }}>
          <Typography
            sx={{ fontWeight: 400, fontSize: "16px", color: "#333333" }}
          >
            Are you sure you want to delete this item?
          </Typography>
          <Typography
            variant="body2"
            color="#333333"
            sx={{ fontSize: "16px", mt: 1 }}
          >
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: "16px 24px" }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{
              fontWeight: "600",
              textTransform: "capitalize",
              padding: "10px 20px",
              border: "1px solid #1570EF",
              color: "#1570EF",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            sx={{
              fontWeight: "600",
              textTransform: "capitalize",
              padding: "10px 20px",
              background: "#1570EF",
              color: "#ffffff",
              borderRadius: "8px",
              "&:hover": {
                background: "#1570EF",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* New Status Confirmation Dialog */}
      <Dialog
        open={openStatusDialog}
        onClose={handleCloseStatusDialog}
        sx={{
          "& .MuiDialog-paper": {
            width: "450px",
            maxWidth: "450px",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontSize: "24px",
            fontWeight: "600",
            mt: 1.5,
          }}
        >
          {currentPlan?.is_active ? "Deactivate this plan" : "Active this plan"}
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", my: 2 }}>
          <Typography
            sx={{ fontWeight: 400, fontSize: "16px", color: "#333333" }}
          >
            Are you sure you want to{" "}
            {currentPlan?.is_active ? "deactivate" : "activate"} this plan?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: "16px 24px" }}>
          <Button
            onClick={handleCloseStatusDialog}
            sx={{
              fontWeight: "600",
              textTransform: "capitalize",
              padding: "10px 20px",
              border: "1px solid #1570EF",
              color: "#1570EF",
              borderRadius: "8px",
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={handleTogglePlanStatus}
            sx={{
              fontWeight: "600",
              textTransform: "capitalize",
              padding: "10px 20px",
              background: "#1570EF",
              color: "#ffffff",
              borderRadius: "8px",
              "&:hover": {
                background: "#1570EF",
              },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subscription;
